import type { StateCreator } from 'zustand'

export interface WorkspaceTab {
  /** Stable tab identity (tabIdFor): routeId, plus param values when any. */
  id: string
  /** Manifest entry kind, e.g. 'duty.detail'. */
  routeId: string
  title: string
  /** Canonical tab pathname + task-owned query + hash. */
  href: string
}

export interface CloseTabResult {
  /** False when the id wasn't open — nothing was removed. */
  removed: boolean
  /**
   * Href of the left-neighbour tab the removal clamps to, or `null` when the
   * list is now empty. Pure list geometry — whether to navigate is the
   * caller's call.
   */
  neighborHref: string | null
}

interface TabsState {
  items: WorkspaceTab[]
}

export interface TabsSlice {
  tabs: TabsState
  openTab: (tab: WorkspaceTab) => void
  renameTab: (id: string, title: string) => void
  /** Records the tab's latest href (query/hash included). No-op when equal. */
  updateTabHref: (id: string, href: string) => void
  /** Removes the tab and reports the left-neighbour clamp. */
  closeTab: (id: string) => CloseTabResult
}

export const createTabsSlice: StateCreator<TabsSlice, [], [], TabsSlice> = (
  set,
  get
) => ({
  tabs: {
    items: [],
  },

  openTab: (tab) =>
    set((s) => {
      // Append-only; never overwrites an existing tab's title (a placeholder
      // must not clobber a real name set by renameTab).
      const idx = s.tabs.items.findIndex((t) => t.id === tab.id)
      if (idx !== -1) return s
      return { tabs: { ...s.tabs, items: [...s.tabs.items, tab] } }
    }),

  renameTab: (id, title) =>
    set((s) => {
      const idx = s.tabs.items.findIndex((t) => t.id === id)
      if (idx === -1 || s.tabs.items[idx]!.title === title) return s
      const nextItems = [...s.tabs.items]
      nextItems[idx] = { ...nextItems[idx]!, title }
      return { tabs: { ...s.tabs, items: nextItems } }
    }),

  updateTabHref: (id, href) =>
    set((s) => {
      const idx = s.tabs.items.findIndex((t) => t.id === id)
      if (idx === -1 || s.tabs.items[idx]!.href === href) return s
      const nextItems = [...s.tabs.items]
      nextItems[idx] = { ...nextItems[idx]!, href }
      return { tabs: { ...s.tabs, items: nextItems } }
    }),

  closeTab: (id) => {
    const idx = get().tabs.items.findIndex((t) => t.id === id)
    if (idx === -1) return { removed: false, neighborHref: null }

    const filtered = get().tabs.items.filter((t) => t.id !== id)
    set((s) => ({ tabs: { ...s.tabs, items: filtered } }))

    if (filtered.length === 0) return { removed: true, neighborHref: null }
    // Left-neighbour fallback, clamped to the surviving list.
    const neighborIdx = Math.min(Math.max(idx - 1, 0), filtered.length - 1)
    return { removed: true, neighborHref: filtered[neighborIdx]!.href }
  },
})
